/**
 * Helpers for the markers the server stamps into a document description.
 *
 * The markers are plain Hebrew text stored inside the description itself (see
 * OcrMisfiledDocumentDetector / the duplicate-document markers on the server), so detection is
 * a substring match on the description.
 */

/** Set by the OCR import when the counterparty of the document is the client themselves. */
export const MISFILED_DESCRIPTION_MARKERS = {
    incomeUploadedAsExpense: 'מסמך הכנסה שהועלה בהוצאות',
    expenseUploadedAsIncome: 'מסמך הוצאה שהועלה בהכנסות',
};

const DUPLICATE_MARKERS = ['כפולה ל מסמך שמספרו', 'מסמך כפול למספר'];
const DUPLICATED_FROM_MARKER = 'שוכפל מ';
/** Set on every document created by splitting another one (see DocumentSplitRules.DescriptionPrefix). */
const SPLIT_FROM_MARKER = 'פוצל ממסמך מספר';

export const isDuplicatedFromDescription = (description) =>
    String(description ?? '').includes(DUPLICATED_FROM_MARKER);

export const isSplitFromDescription = (description) =>
    String(description ?? '').includes(SPLIT_FROM_MARKER);

export const isDuplicateDescription = (description) => {
    const text = String(description ?? '');
    return DUPLICATE_MARKERS.some((marker) => text.includes(marker));
};

/**
 * Tells whether the document was uploaded under the wrong type, and which type it belongs to.
 *
 * @returns {{ isMisfiled: boolean, expectedType: 'Incomes'|'Expenses'|null, messageKey: string|null }}
 */
export const getMisfiledDocumentFlag = (description) => {
    const text = String(description ?? '');

    if (text.includes(MISFILED_DESCRIPTION_MARKERS.incomeUploadedAsExpense)) {
        return {
            isMisfiled: true,
            expectedType: 'Incomes',
            messageKey: 'IncomeDocumentUploadedToExpenses',
        };
    }

    if (text.includes(MISFILED_DESCRIPTION_MARKERS.expenseUploadedAsIncome)) {
        return {
            isMisfiled: true,
            expectedType: 'Expenses',
            messageKey: 'ExpenseDocumentUploadedToIncomes',
        };
    }

    return { isMisfiled: false, expectedType: null, messageKey: null };
};

/** Highlight class for a description cell — a wrong document type outranks the duplicate flags. */
export const getDescriptionCellClass = (description) => {
    if (getMisfiledDocumentFlag(description).isMisfiled) return 'misfiled-document-cell';
    if (isDuplicatedFromDescription(description)) return 'duplicate-from-cell';
    if (isDuplicateDescription(description)) return 'flickering-cell';
    if (isSplitFromDescription(description)) return 'split-from-cell';
    return '';
};
